import {
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import './EngagementTable.css'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState, useRef } from 'react'
import { Box, textAlign } from '@mui/system'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { useNavigate } from 'react-router-dom'
import { getOngoingSevaIntiativesListByMpId } from 'store/action/ongoingSevaInitiativesList'
import { getIds } from 'Components/ReusableComponents.js/getIds'
import { getEngagementMpData } from 'store/action/getEngagemnetData'
import { useNotificationContext } from 'utils/NotificationContext'
import { useLoading } from 'utils/LoadingContext'
import { resetEngagementMpData } from 'store/action/getEngagemnetData'

const EngagementTable = ({ setTableLoad, selectedCard }) => {
  const loggedInId = getIds()
  const dispatch = useDispatch()
  const mpLists = useSelector((state) => state?.getEngagementMpData?.data)
  const mpList = mpLists?.slice(9) || []
  const navigate = useNavigate()
  const mpProfileData = useSelector((state) => state?.mpProfileData?.data[0])
  const [columns, setColumns] = useState([])
  const [page, setPage] = useState(1)
  const { setLoading } = useLoading()
  const { showNotification } = useNotificationContext()

  const scrollRef = useRef(null)
  let rankNumber = 9 // Initialize rankNumber to 9 as in StickyHeadTable
  let prevDenseRank = mpLists?.[8]?.dense_rank // Initialize prevDenseRank with the dense_rank of the 9th item

  const generateRankNumber = (item) => {
    let newRankNumber = rankNumber
    let newPrevDenseRank = prevDenseRank

    if (item?.dense_rank === prevDenseRank) {
      newPrevDenseRank = item?.dense_rank
    } else {
      newRankNumber += 1
      newPrevDenseRank = item?.dense_rank
    }
    rankNumber = newRankNumber
    prevDenseRank = newPrevDenseRank
    return newRankNumber
  }

  useEffect(() => {
    const fetchData = async () => {
      if (selectedCard?.id && page !== 1) {
        try {
          setTableLoad(true)
          await dispatch(getEngagementMpData(selectedCard?.id, page))
        } catch (error) {
          showNotification('Error', error.message || JSON.stringify(error))
        } finally {
          setTableLoad(false)
        }
      }
    }

    fetchData()
  }, [page])

  useEffect(() => {
    setColumns(selectedCard?.labels?.split(','))
    setPage(1)
  }, [selectedCard])

  const onScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current

      if (
        Math.floor(scrollTop) + clientHeight === scrollHeight ||
        Math.floor(scrollTop) + clientHeight + 1 === scrollHeight ||
        Math.ceil(scrollTop) + clientHeight === scrollHeight
      ) {
        if (selectedCard?.id) {
          setPage((prev) => prev + 1)
        }
      }
    }
  }

  const handleNavigate = async (item) => {
    if (item?.hasOwnProperty('isMP') && item?.isMP == 0) {
      return
    }
    await dispatch(getOngoingSevaIntiativesListByMpId({ id: item.id }))
    if (item.id === loggedInId) {
      navigate('/MySevaUpdates', {
        state: {
          MpName: 'My Seva Rank',
          mpId: loggedInId,
        },
      })
    } else {
      navigate('/MpSevaUpdates', {
        state: {
          MpName: item.user_name,
          mpId: item.id,
          mpClick: true,
          mpData: item && item,
        },
      })
    }
  }

  const headerCellStyle = {
    backgroundColor: '#e3f5ff',
    borderBottom: '3px solid #356f92',
    fontWeight: 'bold',
    fontFamily: '"HK Grotesk", sans-serif',
    fontSize: '16px',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }

  return (
    <>
      {mpList ? (
        <Box
          sx={{
            borderRadius: '20px',
            boxShadow: 'none',
            overflow: 'visible',
            mb: 3,
          }}
        >
          <Paper className="engagement_table_parent mt-5" sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            <TableContainer
              ref={scrollRef}
              onScroll={onScroll}
              sx={{
                borderRadius: '20px',
                height: 560,
                '&::-webkit-scrollbar': {
                  display: 'none', // Hides the scrollbar in WebKit browsers
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {selectedCard?.rankEnabled && <TableCell sx={headerCellStyle}>Rank</TableCell>}
                    <TableCell sx={headerCellStyle}></TableCell>
                    <TableCell sx={headerCellStyle}>MP Name</TableCell>
                    <TableCell sx={headerCellStyle}>Constituency</TableCell>
                    {columns &&
                      columns.map((item, index) => {
                        return (
                          <TableCell key={index} sx={{ ...headerCellStyle, textAlign: 'center' }}>
                            {item}
                          </TableCell>
                        )
                      })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mpList.map((item, index) => {
                    // Check if this is the last row
                    const isLastRow = index === mpList.length - 1

                    return (
                      <TableRow
                        onClick={() => handleNavigate(item)}
                        key={index}
                        sx={{
                          '&:hover': {
                            background: 'rgb(227, 245, 255)',
                          },
                          cursor: 'pointer',
                        }}
                      >
                        {selectedCard?.rankEnabled && (
                          <TableCell className="rank_card">{generateRankNumber(item)}</TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ padding: '0px', display: 'flex', justifyContent: 'center' }}>
                            {item.user_avatar ? (
                              <Avatar
                                alt="avatar"
                                src={item.user_avatar}
                                className="img-circle leader-circle-img mr-1 item-user-avatar"
                                draggable="false"
                                sx={{ padding: '0px' }}
                              />
                            ) : (
                              <Box>
                                <AccountCircleIcon
                                  sx={{
                                    fontSize: 'xx-large',
                                    width: '50px',
                                    height: '50px',
                                    color: '#EEEEEE',
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="table-name-container">
                            <Box>
                              <Typography variant="body1" className="item-user-name">
                                {item.user_name}
                              </Typography>
                              <Typography variant="body1" className="item-party">
                                {item.designation}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{item?.constituency_name}</TableCell>
                        {columns &&
                          columns.map((columnName, colIndex) => {
                            return (
                              <TableCell key={colIndex} className="engagement_userdata">
                                {item[columnName]}
                              </TableCell>
                            )
                          })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : (
        <></>
      )}
    </>
  )
}

export default EngagementTable
